import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, Send, ArrowLeft, Check } from 'lucide-react';

const ContactPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    organization: '',
    category: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const categories = [
    'Investor Enquiries',
    'Partnership Opportunities',
    'Media Enquiries',
    'General Support',
    'Beta Tester Questions'
  ];

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${BACKEND_URL}/api/contact/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          organization: '',
          category: '',
          message: ''
        });
      } else {
        if (process.env.NODE_ENV !== 'production') {
          console.error('Contact form error:', data);
        }
        setSubmitStatus('error');
      }
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') {
        console.error('Network error:', error);
      }
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505]" style={{fontFamily: "'DM Sans', sans-serif"}}>
      {/* Header Navigation */}
      <header className="fixed top-0 left-0 right-0 z-50 py-4 px-4 sm:px-6 lg:px-8 backdrop-blur-2xl bg-[#050505]/70 border-b border-zinc-800/50">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <button
            onClick={() => navigate('/')}
            className="text-white font-black text-2xl hover:opacity-80 transition-opacity"
            style={{fontFamily: "'Outfit', sans-serif"}}
          >
            Flipping Friends
          </button>
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </button>
        </div>
      </header>

      {/* Main Content */}
      <section className="pt-32 pb-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-[#00D26A]/20 border border-[#00D26A]/30 text-[#00D26A] px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Mail className="w-4 h-4" />
              Get in Touch
            </div>
            
            <h1 className="text-4xl sm:text-5xl font-black text-white mb-4 tracking-tighter" style={{fontFamily: "'Outfit', sans-serif"}}>
              Contact <span className="bg-clip-text text-transparent bg-gradient-to-r from-[#00D26A] to-[#FFD700]">Flipping Friends</span>
            </h1>
            
            <p className="text-xl text-zinc-400 leading-relaxed">
              Whether you're an investor, partner, or just curious—we'd love to hear from you.
            </p>
          </div>

          {/* Contact Form */}
          {submitStatus === 'success' ? (
            <div className="bg-gradient-to-r from-[#00D26A] to-[#FFD700] text-black p-8 rounded-2xl text-center shadow-lg">
              <Check className="w-16 h-16 mx-auto mb-4" />
              <h3 className="text-2xl font-bold mb-2">Message Sent Successfully!</h3>
              <p className="mb-6">We'll get back to you within 48 hours.</p>
              <button
                onClick={() => navigate('/')}
                className="bg-black text-white px-6 py-3 rounded-full font-bold hover:opacity-90 transition-opacity"
              >
                Back to Home
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="bg-[#0A0A0A] border border-zinc-800/50 rounded-2xl p-8 shadow-lg space-y-6">
              {/* Name */}
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-white mb-2">
                  Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  required
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Your full name"
                  className="w-full px-4 py-3 rounded-lg border border-zinc-700 bg-[#050505] text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#00D26A]"
                />
              </div>

              {/* Email */}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-white mb-2">
                  Email *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="your@email.com"
                  className="w-full px-4 py-3 rounded-lg border border-zinc-700 bg-[#050505] text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#00D26A]"
                />
              </div>

              {/* Organization (Optional) */}
              <div>
                <label htmlFor="organization" className="block text-sm font-medium text-white mb-2">
                  Organization <span className="text-zinc-500">(optional)</span>
                </label>
                <input
                  type="text"
                  id="organization"
                  name="organization"
                  value={formData.organization}
                  onChange={handleChange}
                  placeholder="Company or organization name"
                  className="w-full px-4 py-3 rounded-lg border border-zinc-700 bg-[#050505] text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#00D26A]"
                />
              </div>

              {/* Category */}
              <div>
                <label htmlFor="category" className="block text-sm font-medium text-white mb-2">
                  Enquiry Category *
                </label>
                <select
                  id="category"
                  name="category"
                  required
                  value={formData.category}
                  onChange={handleChange}
                  className="w-full px-4 py-3 rounded-lg border border-zinc-700 bg-[#050505] text-white focus:outline-none focus:ring-2 focus:ring-[#00D26A]"
                >
                  <option value="">Select a category...</option>
                  {categories.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>

              {/* Message */}
              <div>
                <label htmlFor="message" className="block text-sm font-medium text-white mb-2">
                  Message *
                </label>
                <textarea
                  id="message"
                  name="message"
                  required
                  value={formData.message}
                  onChange={handleChange}
                  rows="6"
                  placeholder="Tell us more about your enquiry..."
                  className="w-full px-4 py-3 rounded-lg border border-zinc-700 bg-[#050505] text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#00D26A] resize-none"
                />
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-gradient-to-r from-[#00D26A] to-[#FFD700] hover:opacity-90 disabled:opacity-50 text-black font-bold px-8 py-4 rounded-full text-lg transition-all transform hover:translate-y-[-2px] shadow-[0_0_30px_rgba(0,210,106,0.3)] disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isSubmitting ? (
                  'Sending...'
                ) : (
                  <>
                    <Send className="w-5 h-5" />
                    Send Message
                  </>
                )}
              </button>

              {submitStatus === 'error' && (
                <p className="text-red-400 text-center text-sm">
                  Something went wrong. Please try again or email marcus@flippingfriends.co.uk directly.
                </p>
              )}

              <p className="text-center text-zinc-500 text-sm">
                We typically respond within 48 hours
              </p>
            </form>
          )}

          {/* Alternative Contact */}
          <div className="mt-12 text-center">
            <p className="text-zinc-400 mb-4">Prefer to reach out directly?</p>
            <div className="space-y-2 text-sm">
              <p className="text-white">
                📧 Email: <a href="mailto:marcus@flippingfriends.co.uk" className="text-[#00D26A] hover:text-[#FFD700] underline">marcus@flippingfriends.co.uk</a>
              </p>
              <p className="text-white">
                📱 Phone: <a href="tel:07375084884" className="text-[#00D26A] hover:text-[#FFD700] underline">07375 084884</a>
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
